import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

import { isEqual } from 'lodash-es';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { MetricsChips } from '@/Pages/DatasetSessionCompare/components/AggregateFeedbackChips';
import { AggregateFeedbackChipsWithExpandButton } from '@/Pages/DatasetSessionCompare/components/AggregateFeedbackChipsWithExpandButton';
import { DatasetSessionCompareTable } from '@/Pages/DatasetSessionCompare/components/DatasetSessionCompareTable';
import { FeedbackChipsWithExpandButton } from '@/Pages/DatasetSessionCompare/components/FeedbackChipsWithExpandButton';
import {
  COMPARISON_VIEW_MAX_NUM_CATEGORIES_TO_SHOW,
  DatasetSessionCompareSettings,
} from '@/Pages/DatasetSessionCompare/constants';
import { isRealRun } from '@/Pages/DatasetSessionCompare/utils/isRealRun';
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { RunFeedbackButtons } from '@/components/RunFeedbackButtons';
import useToast from '@/components/Toast/useToast';
import { useDeleteExample } from '@/hooks/useSwr';
import {
  ExampleSchemaWithRunsAndOptionalFields,
  RunSchema,
} from '@/types/schema';
import { getTopLevelError } from '@/utils/stringUtils';
import { useLocalStorageState } from '@/utils/use-local-storage-state';

import { PlaygroundChainOutput } from '../PlaygroundChainOutput';
import { PlaygroundChatModelOutput } from '../PlaygroundChatModelOutput';
import {
  useDeep,
  usePlaygroundStore,
} from '../PlaygroundHome/hooks/usePlaygroundStore';
import { PlaygroundLatency } from '../components/PlaygroundLatency';
import { PlaygroundRunLink } from '../components/PlaygroundStats';
import { isChatModel } from '../utils/Playground.utils';

function PlaygroundOutputPaginationControl({
  maxLength,
  currentIndex,
  setCurrentIndex,
}: {
  maxLength: number;
  currentIndex: number;
  setCurrentIndex: (value: number) => void;
}) {
  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const goToNext = () => {
    if (currentIndex < maxLength - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const isFirstSlide = currentIndex === 0;
  const isLastSlide = currentIndex === maxLength - 1;

  if (maxLength === 1) {
    return null;
  }

  return (
    <div className="mb-auto flex items-center">
      <button
        onClick={goToPrevious}
        disabled={isFirstSlide}
        type="button"
        className="rounded-md p-1.5 text-ls-black hover:bg-secondary-hover active:bg-secondary disabled:cursor-default disabled:text-ls-gray-300 disabled:hover:bg-transparent"
      >
        <ChevronLeftIcon className="h-5 w-5" />
      </button>
      <span className="w-[1rem] text-center font-medium">
        {currentIndex + 1}
      </span>
      <button
        onClick={goToNext}
        disabled={isLastSlide}
        type="button"
        className="rounded-md p-1.5 text-ls-black hover:bg-secondary-hover active:bg-secondary disabled:cursor-default disabled:text-ls-gray-300 disabled:hover:bg-transparent"
      >
        <ChevronRightIcon className="h-5 w-5" />
      </button>
    </div>
  );
}

function PlaygroundSingleManualOutput({
  runs,
  isChat,
  expectedFeedbackKeys,
  loadingState,
  error,
}: {
  runs?: Array<RunSchema>;
  isChat: boolean;
  expectedFeedbackKeys?: string[];
  loadingState?: 'loading' | 'done';
  error?: Error | null;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { datasetShareToken } = useParams();

  // reset current index to 0 when we have an index higher than the length of runs so that we don't try to index into something that doesn't exist
  useEffect(() => {
    if (runs?.length && currentIndex >= runs?.length) {
      setCurrentIndex(0);
    }
  }, [runs, currentIndex]);

  const setCurrentIndexIfExists = useCallback(
    (index: number) => {
      if (!runs || runs.length === 0) {
        return;
      }

      if (index < runs.length) {
        setCurrentIndex(index);
      }
    },
    [runs]
  );

  const [feedbackChipsExpanded, setFeedbackChipsExpanded] =
    useLocalStorageState('ls:comparisonViewFeedbackChipsExpanded', false);

  return (
    <div className="mx-6 my-3 flex flex-col justify-end gap-3">
      {error && (
        <ExpandableErrorAlert error={getTopLevelError(error.toString())} />
      )}
      {runs && runs.length > 0 && runs[currentIndex] ? (
        <>
          {isChat ? (
            <PlaygroundChatModelOutput run={runs[currentIndex]} isInputHidden />
          ) : (
            <PlaygroundChainOutput run={runs[currentIndex]} isInputHidden />
          )}

          <div className="flex">
            <PlaygroundOutputPaginationControl
              maxLength={runs.length}
              currentIndex={currentIndex}
              setCurrentIndex={setCurrentIndexIfExists}
            />
            <div className="mb-auto ml-auto flex flex-wrap gap-2 whitespace-nowrap">
              <FeedbackChipsWithExpandButton
                allowTruncation
                sortMode="key"
                expanded={feedbackChipsExpanded}
                setExpanded={setFeedbackChipsExpanded}
                feedbackStats={runs[currentIndex].feedback_stats}
                feedbackSourceRunId={runs[currentIndex].id}
                showNotes
                className="rounded-md bg-background"
                disablePopover={true}
                maxNumberOfCategories={
                  COMPARISON_VIEW_MAX_NUM_CATEGORIES_TO_SHOW
                }
                expectedFeedbackKeys={expectedFeedbackKeys}
              />
              {(!runs[currentIndex] || !isRealRun(runs[currentIndex])) && ( // using status here to indicate if the run has been persisted or not
                <PlaygroundLatency
                  loadingState={loadingState}
                  startDate={new Date(runs[currentIndex].start_time)}
                />
              )}
              {runs[currentIndex] &&
                runs[currentIndex]?.status &&
                !datasetShareToken && (
                  <RunFeedbackButtons size="xs" run={runs[currentIndex]} />
                )}
              {runs[currentIndex] && isRealRun(runs[currentIndex]) && (
                <MetricsChips run={runs[currentIndex]} />
              )}
              <PlaygroundRunLink run={runs[currentIndex]} />
            </div>
          </div>
        </>
      ) : (
        <div className="rounded-md bg-secondary p-3 text-sm">
          {loadingState === 'loading'
            ? 'Loading...'
            : 'Click Start to run generation...'}
        </div>
      )}
    </div>
  );
}

export const PlaygroundExperimentView = memo(
  ({
    datasetId,
    displaySettingsState,
    exampleRuns,
    isEditable,
  }: {
    datasetId: string | undefined;
    displaySettingsState: DatasetSessionCompareSettings;
    exampleRuns: ExampleSchemaWithRunsAndOptionalFields[];
    isEditable: boolean;
  }) => {
    const { createToast } = useToast();
    const promptsInfo = usePlaygroundStore(
      useDeep((state) =>
        state.prompts.map((p) => ({
          sessionId: p.sessionId,
          columnId: p.columnId,
        }))
      )
    );
    const inputSourceType = usePlaygroundStore(
      (state) => state.inputSourceType
    );
    const lastManifestKwargs = usePlaygroundStore(
      useDeep((state) => state.prompts[0]?.manifest.kwargs.last)
    );
    const isCreatingNewDataset = usePlaygroundStore(
      (state) => state.isCreatingNewDataset
    );
    const evaluatorFeedbackKeys = usePlaygroundStore(
      (state) => state.evaluatorFeedbackKeys
    );
    const expectedFeedbackKeys = usePlaygroundStore(
      (state) => state.expectedFeedbackKeys
    );

    const loadingState = usePlaygroundStore((state) => state.loadingState);
    const setExample = usePlaygroundStore((state) => state.setExample);
    const removeExampleFromDataset = usePlaygroundStore(
      (state) => state.removeExampleFromDataset
    );
    const decreaseExampleCount = usePlaygroundStore(
      (state) => state.decreaseExampleCount
    );
    const removeReferenceOutputFromExamples = usePlaygroundStore(
      (state) => state.removeReferenceOutputFromExamples
    );

    const [pendingDeleteExampleId, setPendingDeleteExampleId] = useState<
      string | undefined
    >(undefined);

    const [expanded, setExpanded] = useState(false);
    const columns = useMemo(
      () =>
        promptsInfo.map((p, index) =>
          p.sessionId
            ? {
                sessionId: p.sessionId,
                columnName: (
                  <div className="flex items-center gap-4">
                    {exampleRuns?.[0]?.runs && (
                      <div className="flex shrink-0 flex-wrap items-center gap-2">
                        {promptsInfo.length > 1
                          ? `Output ${String.fromCharCode(
                              'A'.charCodeAt(0) + index
                            )}`
                          : 'Output'}
                      </div>
                    )}
                    {datasetId && (
                      <div className="flex flex-wrap items-center gap-1">
                        <AggregateFeedbackChipsWithExpandButton
                          sessionId={p.sessionId}
                          className="rounded-md bg-background"
                          expectedFeedbackKeys={evaluatorFeedbackKeys}
                          disablePopover={true}
                          expanded={expanded}
                          maxChips={1}
                          setExpanded={setExpanded}
                          isNested={true}
                          hiddenFeedbackKeys={[]}
                        />
                      </div>
                    )}
                  </div>
                ),
                disableHeaderClick: true,
              }
            : {
                columnId: p.columnId,
                columnName:
                  promptsInfo.length > 1
                    ? `Output ${String.fromCharCode('A'.charCodeAt(0) + index)}`
                    : 'Output',
              }
        ) ?? [],
      [promptsInfo, exampleRuns, datasetId, evaluatorFeedbackKeys, expanded]
    );

    const deleteExample = useDeleteExample({
      onSuccess: () => {
        createToast({
          title: 'Example deleted',
          description: 'Example deleted from dataset',
        });
        decreaseExampleCount();
      },
      onError: (error) => {
        createToast({
          title: 'Error deleting example',
          description: error.message,
        });
      },
    });

    if (inputSourceType === 'manual' && promptsInfo.length === 1) {
      // determine if it's chat model from the run instead of the manifest
      // because when we change the model in the ui we don't want to impact the rendering of the output
      const isChat = exampleRuns?.[0]?.runs?.[0]?.serialized?.kwargs?.last
        ? isChatModel(exampleRuns?.[0]?.runs?.[0]?.serialized?.kwargs?.last)
        : isChatModel(lastManifestKwargs);

      return (
        <PlaygroundSingleManualOutput
          runs={exampleRuns?.[0]?.runs}
          isChat={isChat}
          expectedFeedbackKeys={evaluatorFeedbackKeys}
          loadingState={loadingState}
        />
      );
    }

    const displaySettingsStateFinal =
      inputSourceType === 'manual'
        ? {
            ...displaySettingsState,
            isReferenceInputHidden: true,
            isReferenceOutputHidden: true,
          }
        : displaySettingsState;

    return (
      <div className="flex h-full flex-1 flex-col overflow-x-auto">
        <DatasetSessionCompareTable
          expectedFeedbackKeys={expectedFeedbackKeys}
          evaluatorFeedbackKeys={evaluatorFeedbackKeys}
          datasetId={datasetId ?? undefined}
          experimentInProgress={loadingState === 'loading'}
          columns={columns}
          examples={exampleRuns}
          setExample={isEditable ? setExample : undefined}
          onRemoveReferenceOutput={removeReferenceOutputFromExamples}
          areExamplesLoading={false}
          setExamplesSize={() => {
            // do nothing
          }}
          hasMoreToLoad={false}
          isNextPageValidating={false}
          displaySettingsState={displaySettingsStateFinal}
          pageTitle="Playground"
          localStoragePrefix="playground"
          filteringRegressionsOnSession={null}
          regressionsFilteringToImprovements={false}
          datadogPageName="PLAYGROUND_COMPARISON_VIEW"
          useWindowScroll={false}
          hoveredSession={null}
          setHoveredSession={() => {
            // do nothing
          }}
          dataType={'chat'}
          onDeleteExample={
            isEditable
              ? (example) => {
                  if (example.dataset_id) {
                    setPendingDeleteExampleId(example.id);
                  } else {
                    removeExampleFromDataset(example.id);
                  }
                }
              : undefined
          }
          isDeleteLoading={deleteExample.isMutating}
          creatingNew={!!isCreatingNewDataset}
        />
        <DeleteConfirmationModal
          isOpen={!!pendingDeleteExampleId}
          onClose={() => setPendingDeleteExampleId(undefined)}
          onConfirm={() => {
            if (pendingDeleteExampleId) {
              deleteExample.trigger({
                templateUrlParams: { exampleId: pendingDeleteExampleId },
              });
            }
          }}
          title="Delete Example"
          description="Are you sure you want to delete this example? This is irreversible and will delete the example from the dataset."
        />
      </div>
    );
  },
  (prevProps, nextProps) => {
    return isEqual(prevProps, nextProps);
  }
);
